import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Admin.css';
import { FaBars, FaTimes } from 'react-icons/fa';
import useSession from '../hooks/useSession';
import UserAdmin from './UserAdmin';
import NewsAdmin from './NewsAdmin';
import { ApprovalView, RequestsList } from './Approvals';
import { signOut } from '../firebase/auth';
import { auth } from '../firebase/config';
import { Avatar, AccountMenu, nameParts } from '../components/AccountMenu';

const TABS = [
  { id: 'users', label: 'Users' },
  { id: 'requests', label: 'Member Requests' },
  { id: 'news', label: 'Articles' },
];

// Account-card links shown under the admin avatar menu. "Back to site" is
// surfaced as its own header link (mirroring how the public site shows
// "Admin"), not tucked inside this dropdown.
const ACCOUNT_ITEMS = [{ label: 'Settings', to: '/settings' }];

// Brand mark (logo + "Admin"), shared by the top bar and the mobile drawer head.
const AdminBrand = () => (
  <div className="admin-brand">
    <img src="/images/logo.png" alt="Dutchess County Bhabani" className="admin-logo" />
    <span className="admin-title">Admin</span>
  </div>
);

const AdminPage = () => {
  const { loading, user, isAdmin } = useSession();
  const [activeTab, setActiveTab] = useState('users');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen]);

  // Access (auth required) is enforced by ProtectedRoute, so a user is
  // guaranteed. Hold briefly while this hook resolves the claims.
  if (loading || !user) {
    return (
      <div className="admin-shell">
        <div className="admin-center glass">Loading…</div>
      </div>
    );
  }

  if (!isAdmin) return <AdminDenied user={user} />;

  // Email approve/reject links land here: /admin/approve?req=<id>&action=...
  const path = location.pathname.replace(/\/+$/, '');
  if (path === '/admin/approve') {
    const params = new URLSearchParams(location.search);
    return <ApprovalView requestId={params.get('req')} />;
  }

  // Selecting a section from the drawer also closes it.
  const selectTab = (id) => {
    setActiveTab(id);
    setDrawerOpen(false);
  };

  const { first, last } = nameParts(user);
  const fullName = [first, last].filter(Boolean).join(' ');

  return (
    <div className="admin-shell">
      <header className="admin-topbar glass">
        <div className="admin-topbar-inner">
          <AdminBrand />

          {/* Desktop nav */}
          <nav className="admin-nav-desktop" aria-label="Admin sections">
            {TABS.map((t) => (
              <button
                key={t.id}
                className={`admin-tab ${activeTab === t.id ? 'active' : ''}`}
                aria-current={activeTab === t.id ? 'true' : undefined}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
            <Link to="/" className="admin-tab admin-back">
              Site
            </Link>
            <AccountMenu user={user} items={ACCOUNT_ITEMS} />
          </nav>

          {/* Hamburger — mobile only */}
          <button
            className="admin-hamburger"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <FaBars />
          </button>
        </div>
      </header>

      {/* Slide-out drawer (mobile only) */}
      <div
        className={`admin-drawer-overlay ${drawerOpen ? 'show' : ''}`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden={!drawerOpen}
      />
      <aside className={`admin-drawer glass ${drawerOpen ? 'open' : ''}`} aria-hidden={!drawerOpen}>
        <div className="admin-drawer-head">
          <AdminBrand />
          <button
            className="admin-drawer-close"
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
          >
            <FaTimes />
          </button>
        </div>
        <nav className="admin-drawer-nav" aria-label="Admin sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`admin-drawer-link ${activeTab === t.id ? 'active' : ''}`}
              aria-current={activeTab === t.id ? 'true' : undefined}
              onClick={() => selectTab(t.id)}
            >
              {t.label}
            </button>
          ))}
          {/* Same destinations the desktop avatar menu offers, as plain menu
              items here (mirrors the site drawer). */}
          {ACCOUNT_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="admin-drawer-link"
              onClick={() => setDrawerOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <Link to="/" className="admin-drawer-link" onClick={() => setDrawerOpen(false)}>
            Site
          </Link>
        </nav>

        {/* Account block, pinned to the bottom-left of the drawer. */}
        <div className="admin-drawer-foot">
          <div className="admin-drawer-account">
            <Avatar user={user} size="sm" />
            <span className="admin-drawer-name">{fullName}</span>
          </div>
          <button className="admin-drawer-signout" type="button" onClick={() => signOut()}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="admin-main">
        {activeTab === 'users' && <UserAdmin currentUid={user.uid} />}
        {activeTab === 'requests' && <RequestsList />}
        {activeTab === 'news' && <NewsAdmin />}
      </main>
    </div>
  );
};

/* ---------- Access denied ---------- */
const AdminDenied = ({ user }) => {
  const [checking, setChecking] = useState(false);

  const refreshClaims = async () => {
    setChecking(true);
    try {
      // Force a token refresh to pick up a newly-granted admin claim.
      await auth.currentUser.getIdToken(true);
      window.location.reload();
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="admin-shell">
      <div className="admin-center glass admin-denied">
        <h1>Access denied</h1>
        <p>
          You’re signed in as <strong>{user.email || user.uid}</strong>, but this
          account doesn’t have admin access.
        </p>
        <div className="admin-denied-actions">
          <button className="btn-primary" onClick={refreshClaims} disabled={checking}>
            {checking ? 'Checking…' : 'I was just granted admin'}
          </button>
          <button className="admin-link" onClick={() => signOut()}>Sign out</button>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
